/**
 * Pluggable tax layer (PLAN.md §2d, core invariant: "Tax logic stays behind TaxPolicy" —
 * NEVER inline tax rules into the core). The deterministic simulator calls `compute` once
 * per year through this interface and never knows which jurisdiction it is in. Thailand is
 * implemented first (../th); the United States later proves the plug.
 */

/**
 * Everything the tax computation needs for one year. Deliberately broad so the interface
 * is stable as jurisdictions are added; M0 populates only the basics and the Thai-relevant
 * sleeve split. Capital-gains are split by sleeve because SET-listed gains are exempt while
 * the foreign sleeve is taxed on remittance.
 */
export interface YearTaxInput {
  /** Calendar year being assessed. */
  readonly year: number;
  /** Assessable (PIT-relevant) income for the year. */
  readonly assessableIncome: number;
  /** Dividends received (Thai dividend WHT is 10%). ENRICHMENT: now PLUMBED from the plan — the
   *  per-account `dividendYield` accrual summed across accounts each simulated year (0 by default
   *  when no plan sets a yield). */
  readonly dividends: number;
  /** Realised capital gains, split by sleeve. ENRICHMENT: now PLUMBED from the plan — a withdrawal
   *  from a taxable account realises a proportional slice of its embedded gain (average-basis),
   *  routed to `setListed` for `taxable_set`/SET-listed sleeves (exempt) and to `foreign` for
   *  `taxable_foreign` (taxed via remittance). 0 by default when no account carries a `costBasis`
   *  below its balance. */
  readonly capitalGains: {
    /** SET-listed equities sold on the exchange — exempt for Thai individuals. */
    readonly setListed: number;
    /** Foreign sleeve — relevant via the remittance rule. */
    readonly foreign: number;
    /** US (M8) — realised LONG-TERM capital gains on a US taxable brokerage sleeve (`taxable_us`),
     *  taxed at the preferential 0/15/20% rates stacked on ordinary income. OPTIONAL + additive:
     *  absent for Thai / NoTax callers (so Thailand + the M4 goldens are unaffected); 0 by default.
     *  Short-term US gains are ORDINARY income and are folded into `assessableIncome`, not here. */
    readonly longTermUS?: number;
  };
  /** Foreign-sourced income remitted into Thailand this year (Por.161/162). ENRICHMENT: now
   *  PLUMBED — the proceeds of withdrawals/realisations from `taxable_foreign` accounts that are
   *  remitted to Thailand this year (the realised foreign gain is the assessable portion the policy
   *  taxes; the caller folds `capitalGains.foreign` into this remittance, not double-counted). 0 by
   *  default. */
  readonly foreignIncomeRemitted: number;
  /** Withdrawals this year keyed by account kind (drives RMF/SSF clawback checks). */
  readonly withdrawalsByKind: Readonly<Record<string, number>>;
  /** Ages of household members this year (RMF age-55 rule, etc.). */
  readonly ages: readonly number[];

  // --- Optional, ADDITIVE fields (M4). Absent in M0–M3 callers, so NoTaxPolicy and the existing
  //     exact deterministic tests are unaffected; the ThailandTaxPolicy reads them when present. ---
  /** Contributions this year keyed by account kind (drives RMF/SSF/Thai-ESG/PVD deductions). */
  readonly contributionsByKind?: Readonly<Record<string, number>>;
  /** The portion of `assessableIncome` that is EMPLOYMENT income (gets the 50%/THB100k expense
   *  deduction). Defaults to `assessableIncome` when omitted (treats it all as employment). */
  readonly employmentIncome?: number;
  /** Interest income (Thai 15% WHT). Separate from `dividends`. */
  readonly interest?: number;
  /** Per-person allowance metadata for PIT. When omitted, the policy assumes one filer + the
   *  personal allowance only. */
  readonly filing?: {
    /** Whether to claim the spouse allowance (spouse not filing separately). */
    readonly claimSpouse?: boolean;
    /** Number of qualifying children for the child allowance. */
    readonly children?: number;
    /** US (M8) filing status — selects the federal bracket / LTCG-threshold / standard-deduction
     *  schedule. OPTIONAL + additive (Thai/NoTax callers omit it; the US policy defaults to
     *  'single' when absent). The string values match `UsFilingStatus` in tax/us/config. */
    readonly filingStatus?:
      | 'single'
      | 'married_jointly'
      | 'head_of_household'
      | 'married_separately';
  };

  /** Prior calendar year-END balances by account kind (M8) — drives the US RMD calculation
   *  (`priorYearEndBalances.traditional / UniformLifetimeFactor(age)`). OPTIONAL + additive: absent
   *  for Thai / NoTax callers and for any year before an RMD age, so Thailand + the M4 goldens are
   *  unaffected. The core plumbs the traditional sleeve's prior end-of-year balance here. */
  readonly priorYearEndBalances?: Readonly<Record<string, number>>;
}

/** The breakdown of a year's tax. Categories let `explain` show tax line-by-line later. */
export interface TaxResult {
  /** Total tax owed for the year (the number the simulator deducts). */
  readonly total: number;
  /** Tax by category, for drill-down. Keys are jurisdiction-defined. */
  readonly byCategory: Readonly<Record<string, number>>;
  /** Marginal rate that applied, if meaningful (decimal, e.g. 0.2). */
  readonly marginalRate: number;
  /** Any RMF/SSF clawback or early-withdrawal penalty assessed. */
  readonly clawback: number;
}

export interface TaxPolicy {
  readonly jurisdiction: string;
  compute(input: YearTaxInput): TaxResult;
}

/** A zero result helper so policies and the no-op share one shape. */
export function zeroTaxResult(): TaxResult {
  return { total: 0, byCategory: {}, marginalRate: 0, clawback: 0 };
}

/**
 * The default policy: charges nothing. Keeps the deterministic core runnable and testable
 * without any tax assumptions (hand-built zero-return / zero-tax scenarios). The engine
 * uses this unless a plan supplies a real policy.
 */
export class NoTaxPolicy implements TaxPolicy {
  readonly jurisdiction = 'none';
  compute(_input: YearTaxInput): TaxResult {
    return zeroTaxResult();
  }
}
